import React,{useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

// Destructure icons for easy usage 
const {FiArrowRight,FiCheckCircle,FiTarget,FiUser,FiSettings,FiBook,FiAlertCircle,FiExternalLink,FiCpu,FiZap,FiTrendingUp,FiClock,FiActivity,FiStar,FiAward,FiPhone}=FiIcons;

const questions=[
  {id: 'current_ai_use',question: 'How would you describe your current use of AI in your practice?',options: [ "I'm curious but overwhelmed","I've bought tools I never use","I use AI inconsistently","I've implemented some workflows","I want full automation" ]},
  {id: 'organization_level',question: 'How organized are your current systems?',options: [ "Very organized","Organized but clunky","Total chaos","Organized in my head","Depends on the day" ]},
  {id: 'main_goal',question: 'What is your main goal with AI?',options: [ "Save time","Reduce admin","Improve client care","Support my group practice","Scale beyond one to one","I want someone to build it for me" ]},
  {id: 'learning_style',question: 'How do you prefer to learn?',options: [ "Quick templates","Step by step","Done for me","A mix of all three" ]},
  {id: 'biggest_obstacle',question: 'What has been the biggest obstacle so far?',options: [ "Overwhelm","Fear of choosing wrong","No time","Tech confusion","Buying tools I don't use","Not knowing where to start" ]}
];

const pathways={
  1: {name: "Clinical AI Club",description: "For therapists who are overwhelmed, inconsistent, or need structured help implementing simple workflows.",link: "https://www.clinicalaiclub.com",color: "from-blue-500 to-purple-600"},
  2: {name: "Thera AI Hub",description: "For therapists who want a library of GPTs, templates, and tools to use immediately and on their own pace.",link: "https://kymtolson.kartra.com/page/AI-Tools-for-Therapists",color: "from-green-500 to-teal-600"},
  3: {name: "Club + Hub Bundle",description: "For therapists who want both: ongoing support plus the full library of tools.",link: "https://kymtolson.kartra.com/page/AI-Tools-for-Therapists",color: "from-purple-500 to-pink-600"},
  4: {name: "Build a Custom GPT",description: "For therapists who want a specific automation or tool built for them.",link: "https://calendly.com/kymtolson/build-an-ai-gpt-with-kym",color: "from-orange-500 to-red-600"},
  5: {name: "60 Minute Paid Consult",description: "For therapists who need personalized guidance, clarity, or help choosing the right AI direction.",link: "https://calendly.com/kymtolson/60min",color: "from-indigo-500 to-blue-600"},
  6: {name: "60 Day AI Powered Practice Intensive",description: "For therapists who want private coaching and a full AI automation buildout over 60 days.",link: "https://clinicalaiclub.com/the-ai-powered-private-practice-intensive/",color: "from-pink-500 to-rose-600"},
  7: {name: "Discovery Call",description: "For therapists who are unsure which path fits them best.",link: "https://calendly.com/kymtolson/lets-chat-collaboration-meet-up-clone",color: "from-gray-500 to-slate-600"}
};

const LandingPage=({onStart})=> (
  <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0,y: -20}} className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" >
    {/* Abstract Background Shapes */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      <div className="absolute -bottom-32 left-20 w-96 h-96 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
    </div>
    <div className="max-w-4xl w-full z-10">
      <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 md:p-12 text-center text-white overflow-hidden relative">
        {/* Decorative Grid */}
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[length:40px_40px] pointer-events-none"></div>
        <motion.div initial={{scale: 0.9,opacity: 0,y: 20}} animate={{scale: 1,opacity: 1,y: 0}} transition={{delay: 0.2}} className="relative" >
          <div className="inline-flex items-center justify-center p-3 mb-6 rounded-2xl bg-gradient-to-tr from-blue-500 to-purple-500 shadow-lg border border-white/20">
            <SafeIcon icon={FiCpu} className="text-4xl text-white" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
            AI Readiness Assessment
          </h1>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
            Discover the perfect AI strategy for your therapy practice. 
            Get a personalized roadmap in under 2 minutes.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 text-left">
            <FeatureCard icon={FiTarget} title="Tailored Roadmap" desc="Get a custom path based on your specific needs and goals." />
            <FeatureCard icon={FiZap} title="Instant Clarity" desc="Stop the overwhelm and know exactly what to do next." />
            <FeatureCard icon={FiTrendingUp} title="Practice Growth" desc="Unlock tools that help you scale and save time." />
          </div>
          <motion.button onClick={onStart} whileHover={{scale: 1.05,boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1),0 10px 10px -5px rgba(0,0,0,0.04)"}} whileTap={{scale: 0.95}} className="group relative inline-flex items-center gap-3 px-10 py-5 bg-white text-indigo-600 rounded-2xl text-xl font-bold shadow-xl transition-all hover:bg-blue-50" >
            Start Assessment
            <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
          <p className="mt-6 text-sm text-blue-200 opacity-70">
            Takes less than 2 minutes • 100% Free
          </p>
        </motion.div>
      </div>
    </div>
  </motion.div>
);

const FeatureCard=({icon,title,desc})=> (
  <div className="bg-white/5 rounded-2xl p-5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors duration-300">
    <SafeIcon icon={icon} className="text-3xl text-blue-300 mb-3" />
    <h3 className="font-bold text-lg mb-2 text-white">{title}</h3>
    <p className="text-sm text-blue-100 opacity-80 leading-relaxed">{desc}</p>
  </div>
);

function App() {
  const [started,setStarted]=useState(false);
  const [currentQuestion,setCurrentQuestion]=useState(0);
  const [answers,setAnswers]=useState({});
  const [showResults,setShowResults]=useState(false);
  const [recommendation,setRecommendation]=useState(null);

  const handleAnswer=(answer)=> {
    const questionId=questions[currentQuestion].id;
    const newAnswers={...answers,[questionId]: answer};
    setAnswers(newAnswers);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      const pathway=determinePathway(newAnswers);
      setTimeout(()=> {
        setRecommendation(pathway);
        setShowResults(true);
      },500);
    }
  };

  const determinePathway=(answers)=> {
    const {current_ai_use,organization_level,learning_style,main_goal,biggest_obstacle}=answers;
    
    // Calculate pathway scores
    const pathwayScores={
      1: 0, // Clinical AI Club
      2: 0, // Thera AI Hub
      3: 0, // Club + Hub Bundle
      4: 0, // Build a Custom GPT
      5: 0, // 60 Minute Paid Consult
      6: 0, // 60 Day AI Powered Practice Intensive
      7: 0  // Discovery Call
    };

    // Scoring logic
    // Pathway 1: Clinical AI Club (for overwhelm/confusion)
    if (["I'm curious but overwhelmed","I've bought tools I never use","I use AI inconsistently"].includes(current_ai_use)) {
      pathwayScores[1] +=3;
    }
    if (["overwhelm","fear of choosing wrong","not knowing where to start"].includes(biggest_obstacle?.toLowerCase())) {
      pathwayScores[1] +=3;
    }

    // Pathway 2: Thera AI Hub (for immediate tools/templates)
    // MAXIMUM points for Quick templates + Save time combination
    if (learning_style?.toLowerCase()==="quick templates" && main_goal?.toLowerCase()==="save time") {
      pathwayScores[2] +=8; // Maximum score for perfect combination
    } else {
      // Individual scoring if not perfect combo
      if (learning_style?.toLowerCase()==="quick templates") {
        pathwayScores[2] +=3;
      }
      if (main_goal?.toLowerCase()==="save time") {
        pathwayScores[2] +=3;
      }
    }
    
    if (["reduce admin","improve client care"].includes(main_goal?.toLowerCase())) {
      pathwayScores[2] +=2;
    }
    if (["step by step"].includes(learning_style?.toLowerCase())) {
      pathwayScores[2] +=2;
    }
    if (["very organized","organized but clunky"].includes(organization_level?.toLowerCase())) {
      pathwayScores[2] +=1;
    }

    // Pathway 3: Club + Hub Bundle (for both structure and tools)
    if (learning_style?.toLowerCase()==="a mix of all three") {
      pathwayScores[3] +=4;
    }
    if (["very organized","organized but clunky"].includes(organization_level?.toLowerCase()) &&
        ["I'm curious but overwhelmed","I've bought tools I never use","I use AI inconsistently"].includes(current_ai_use)) {
      pathwayScores[3] +=4;
    }

    // Pathway 4: Build a Custom GPT (for done-for-me solutions)
    if (main_goal?.toLowerCase()==="i want someone to build it for me") {
      pathwayScores[4] +=4;
    }
    if (learning_style?.toLowerCase()==="done for me") {
      pathwayScores[4] +=3;
    }
    if (organization_level?.toLowerCase()==="very organized" && 
        main_goal?.toLowerCase()==="scale beyond one to one") {
      pathwayScores[4] +=3;
    }
    if (biggest_obstacle?.toLowerCase()==="no time") {
      pathwayScores[4] +=2;
    }

    // Pathway 5: 60 Minute Paid Consult (for confusion/uncertainty)
    if (biggest_obstacle?.toLowerCase()==="tech confusion") {
      pathwayScores[5] +=3;
    }
    if (Object.values(answers).some(answer=> answer?.toLowerCase().includes("don't know"))) {
      pathwayScores[5] +=3;
    }

    // Pathway 6: 60 Day AI Powered Practice Intensive (for comprehensive needs)
    if (current_ai_use?.toLowerCase()==="i want full automation") {
      pathwayScores[6] +=4;
    }
    if (main_goal?.toLowerCase()==="scale beyond one to one") {
      pathwayScores[6] +=4;
    }
    if (main_goal?.toLowerCase()==="support my group practice") {
      pathwayScores[6] +=3;
    }

    // Pathway 7: Discovery Call (default/backup)
    pathwayScores[7] = 1; // Always available as backup

    // Find top two pathways
    const sortedPathways=Object.entries(pathwayScores)
      .sort((a,b)=> b[1] - a[1])
      .map(([pathwayId,score])=> ({id: parseInt(pathwayId),score}));

    const topPathway=sortedPathways[0];
    const secondPathway=sortedPathways[1];

    // Generate recommendations based on top pathway
    const generateRecommendation=(pathwayId)=> {
      switch(pathwayId) {
        case 1:
          return {
            pathway: 1,
            summary: `You're feeling overwhelmed by AI options and need structured guidance to get started. You've expressed that you're ${current_ai_use?.toLowerCase()} and your biggest challenge is ${biggest_obstacle?.toLowerCase()}.`,
            reason: "The Clinical AI Club is perfect for you because it provides step-by-step guidance without the overwhelm. You'll get structured support to implement AI workflows at a comfortable pace, with a community of therapists who understand exactly where you're coming from.",
            action: "Start with one simple AI workflow this week - try using ChatGPT to help draft session notes for just one client."
          };
        case 2:
          return {
            pathway: 2,
            summary: `You're ready for practical tools you can implement right away. Your goal is to ${main_goal?.toLowerCase()} and you prefer ${learning_style?.toLowerCase()} learning.`,
            reason: "The Thera AI Hub gives you immediate access to a complete library of GPTs, templates, and tools. Since you want to save time and prefer templates, you can dive right in and start using what you need without waiting for coaching calls.",
            action: "Pick one administrative task that takes you the most time and look for a template or tool that could cut that time in half."
          };
        case 3:
          return {
            pathway: 3,
            summary: `You want both structure AND tools - the best of both worlds. You're ${current_ai_use?.toLowerCase()} but you're also ${organization_level?.toLowerCase()}, and you learn best with ${learning_style?.toLowerCase()}.`,
            reason: "The Club + Hub Bundle gives you ongoing support when you need guidance, plus immediate access to all the tools when you're ready to implement. It's perfect for someone who wants flexibility in how they learn and implement AI.",
            action: "Choose one tool from the Hub to try this week, and join the next Club call to get support with implementation."
          };
        case 4:
          return {
            pathway: 4,
            summary: `You want a custom solution built specifically for your practice. Your main goal is ${main_goal?.toLowerCase()} and you prefer ${learning_style?.toLowerCase()} approaches.`,
            reason: "A Custom GPT buildout is perfect because you'll get a tailored solution without having to learn or build anything yourself. This saves you time and gives you exactly what your practice needs.",
            action: "Write down the one workflow or task you most want automated, and be as specific as possible about how it currently works."
          };
        case 5:
          return {
            pathway: 5,
            summary: `Your answers show you need personalized guidance to find the right direction. You're dealing with ${biggest_obstacle?.toLowerCase()} and need clarity on the best path forward.`,
            reason: "A 60-minute consultation will give you personalized recommendations based on your specific practice, goals, and comfort level. You'll leave with a clear action plan tailored just for you.",
            action: "Before your consult, list your top 3 time-consuming tasks and your biggest practice goals for the next 6 months."
          };
        case 6:
          return {
            pathway: 6,
            summary: `You're ready for comprehensive transformation. You want ${main_goal?.toLowerCase()} and are looking for ${current_ai_use?.toLowerCase()}.`,
            reason: "The 60 Day Intensive gives you private coaching plus custom buildouts to completely transform your practice. This is perfect for ambitious goals like scaling or full automation.",
            action: "Map out your current practice workflow from client inquiry to discharge - this will help you see where AI can make the biggest impact."
          };
        case 7:
        default:
          return {
            pathway: 7,
            summary: `Your situation is unique and would benefit from a personalized conversation to determine the best path forward.`,
            reason: "A discovery call will help us understand your specific needs and recommend the perfect combination of tools and support for your practice.",
            action: "Think about what success with AI would look like for your practice in 3 months - this will help guide our conversation."
          };
      }
    };

    const recommendation=generateRecommendation(topPathway.id);
    recommendation.secondRecommendation=secondPathway.id !==topPathway.id ? {
      pathway: secondPathway.id,
      ...pathways[secondPathway.id]
    } : null;

    return recommendation;
  };

  const resetAssessment=()=> {
    setStarted(false);
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setRecommendation(null);
  };

  return (
    <div className="app-container min-h-screen">
      <AnimatePresence mode="wait">
        {!started ? (
          <LandingPage key="landing" onStart={()=> setStarted(true)} />
        ) : !showResults ? (
          <motion.div key="assessment" initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="min-h-screen flex items-center justify-center py-12 px-4" >
            <div className="max-w-2xl w-full">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-white/20">
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-medium text-gray-500">
                      Question {currentQuestion + 1} of {questions.length}
                    </span>
                    <div className="flex space-x-1">
                      {questions.map((_,index)=> (
                        <div key={index} className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${index <=currentQuestion ? 'bg-indigo-600' : 'bg-gray-200'}`} />
                      ))}
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div className="bg-indigo-600 h-2 rounded-full transition-all duration-500 ease-out" style={{width: `${((currentQuestion + 1) / questions.length) * 100}%`}} />
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div key={currentQuestion} initial={{opacity: 0,x: 20}} animate={{opacity: 1,x: 0}} exit={{opacity: 0,x: -20}} transition={{duration: 0.3}} >
                    <h2 className="text-2xl font-bold text-gray-900 mb-8 leading-tight">
                      {questions[currentQuestion].question}
                    </h2>

                    <div className="space-y-3">
                      {questions[currentQuestion].options.map((option,index)=> (
                        <motion.button key={index} onClick={()=> handleAnswer(option)} className="w-full p-5 text-left bg-gray-50 hover:bg-indigo-50 border-2 border-transparent hover:border-indigo-200 rounded-xl transition-all duration-200 flex items-center justify-between group shadow-sm hover:shadow-md" whileHover={{scale: 1.01}} whileTap={{scale: 0.99}} >
                          <span className="text-lg text-gray-700 group-hover:text-indigo-900 font-medium">
                            {option}
                          </span>
                          <SafeIcon icon={FiArrowRight} className="text-gray-300 group-hover:text-indigo-600 transform translate-x-[-10px] opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300" />
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
              
              <div className="mt-8 text-center">
                <button onClick={()=> setStarted(false)} className="text-white/60 hover:text-white text-sm font-medium transition-colors" >
                  ← Back to Home
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="results" initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="min-h-screen flex items-center justify-center py-12 px-4" >
            {recommendation && (
              <div className="max-w-4xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
                <div className={`bg-gradient-to-r ${pathways[recommendation.pathway].color} p-8 text-white text-center`}>
                  <SafeIcon icon={FiStar} className="text-6xl mx-auto mb-4 text-white/90" />
                  <h1 className="text-3xl font-bold mb-2">Your Top Recommendation</h1>
                  <h2 className="text-4xl font-extrabold mb-4">{pathways[recommendation.pathway].name}</h2>
                  <p className="text-xl opacity-90 max-w-2xl mx-auto">{pathways[recommendation.pathway].description}</p>
                </div>

                <div className="p-8 md:p-12 bg-white">
                  <div className="grid md:grid-cols-2 gap-8 mb-10">
                    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <SafeIcon icon={FiUser} className="mr-2 text-indigo-600" />
                        Your Profile
                      </h3>
                      <p className="text-gray-600 leading-relaxed">{recommendation.summary}</p>
                    </div>
                    
                    <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <SafeIcon icon={FiTarget} className="mr-2 text-blue-600" />
                        Why It Fits
                      </h3>
                      <p className="text-gray-600 leading-relaxed">{recommendation.reason}</p>
                    </div>
                  </div>

                  <div className="bg-yellow-50 rounded-2xl p-6 border border-yellow-100 mb-10 flex items-start">
                    <SafeIcon icon={FiActivity} className="text-2xl text-yellow-600 mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">One Action to Take Today</h3>
                      <p className="text-gray-700">{recommendation.action}</p>
                    </div>
                  </div>

                  {/* Second Recommendation */}
                  {recommendation.secondRecommendation && (
                    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 mb-10">
                      <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center">
                        <SafeIcon icon={FiAward} className="mr-2 text-gray-500" />
                        Alternative Option
                      </h3>
                      <h4 className="text-md font-semibold text-gray-800 mb-2">
                        {recommendation.secondRecommendation.name}
                      </h4>
                      <p className="text-sm text-gray-600 mb-4">
                        {recommendation.secondRecommendation.description}
                      </p>
                      <a href={recommendation.secondRecommendation.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium text-sm" >
                        Learn More
                        <SafeIcon icon={FiExternalLink} className="ml-1 text-xs" />
                      </a>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                    <motion.a href={pathways[recommendation.pathway].link} target="_blank" rel="noopener noreferrer" className={`flex-1 max-w-sm bg-gradient-to-r ${pathways[recommendation.pathway].color} text-white px-8 py-4 rounded-xl font-bold text-lg text-center hover:shadow-lg transition-all flex items-center justify-center`} whileHover={{scale: 1.02}} whileTap={{scale: 0.98}} >
                      Get Started Now
                      <SafeIcon icon={FiExternalLink} className="ml-2" />
                    </motion.a>
                    
                    <motion.button onClick={resetAssessment} className="px-8 py-4 border-2 border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition-colors" whileHover={{scale: 1.02}} whileTap={{scale: 0.98}} >
                      Start Over
                    </motion.button>
                  </div>

                  {/* Discovery Call Button - Always Available */}
                  <div className="border-t border-gray-200 pt-6 mt-8">
                    <div className="text-center">
                      <p className="text-gray-500 text-sm mb-3">
                        Still unsure? Want to talk through your options?
                      </p>
                      <motion.a 
                        href="https://calendly.com/kymtolson/lets-chat-collaboration-meet-up-clone" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                        whileHover={{scale: 1.02}}
                        whileTap={{scale: 0.98}}
                      >
                        <SafeIcon icon={FiPhone} className="text-lg" />
                        Book a Discovery Call
                      </motion.a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;